/**
 * Gate - Multiplier gate that increases army size
 *
 * Refined readability pass:
 * - Vector-drawn frame with transparent window and defined borders
 * - Text-based multiplier label for consistent legibility
 * - Subtle idle pulse + approach response
 */

import Phaser from 'phaser';
import {
  COLORS,
  GATE_LAYOUT,
  ENTITY_PIXELS,
  FONTS,
  GAME_HEIGHT,
  SAFE_AREA,
} from '../config';
import { ARMY_HARD_CAP, EFFECTS, getEffectiveMultiplier } from '../utils/constants';
import { GateOperation } from '../levels/LevelGenerator';
import { getParticlePool } from '../systems/ParticlePool';
import { drawChamferedPanel } from '../ui/SceneChrome';

export interface GateConfig {
  x: number;
  y: number;
  operation?: GateOperation;
  multiplier?: number;
}

export class Gate extends Phaser.GameObjects.Container {
  /** Gate dimensions */
  static readonly WIDTH = ENTITY_PIXELS.gateWidth;   // ~154px (41% of 375)
  static readonly HEIGHT = ENTITY_PIXELS.gateHeight; // ~86px (10.6% of 812)
  /** Minimum top edge for gate spawn in slice mode to keep labels readable under HUD */
  static readonly SLICE_READABLE_TOP_Y = SAFE_AREA.top + 96;
  /** Default slice spawn center Y (top edge + half gate height) */
  static readonly SLICE_SPAWN_Y = Gate.SLICE_READABLE_TOP_Y + Gate.HEIGHT / 2;

  /** Gate operation payload */
  private operation: GateOperation;

  /** Whether this gate has been activated */
  private isActivated: boolean = false;

  /** Whether this gate is still active (not yet passed) */
  private isActive: boolean = true;

  /** Layered frame graphics */
  private frameGraphics: Phaser.GameObjects.Graphics;
  private trimGraphics: Phaser.GameObjects.Graphics;

  /** Multiplier badge + label */
  private multiplierPrefix: Phaser.GameObjects.Text;
  private multiplierPlate: Phaser.GameObjects.Graphics;

  /** Reference to scroll speed for movement */
  private scrollSpeed: number = 100;
  private approachIntensity: number = 0;
  private pulseOffsetMs: number;
  private accentColor: number;

  constructor(scene: Phaser.Scene, config: GateConfig) {
    super(scene, config.x, config.y);

    this.operation = this.resolveOperation(config);
    this.accentColor = this.getOperationColor(this.operation);
    this.pulseOffsetMs = Phaser.Math.Between(0, 900);

    this.frameGraphics = scene.add.graphics();
    this.trimGraphics = scene.add.graphics();
    this.multiplierPlate = scene.add.graphics();
    this.multiplierPrefix = scene.add.text(0, 0, this.getOperationLabel(this.operation), {
      fontFamily: FONTS.display,
      fontSize: '28px',
      color: '#FFFFFF',
      fontStyle: 'bold',
      stroke: '#161A20',
      strokeThickness: 5,
      shadow: {
        offsetX: 0,
        offsetY: 2,
        color: '#00000066',
        blur: 0,
        fill: true,
      },
    }).setOrigin(0.5);
    this.multiplierPrefix.setResolution(2);

    this.drawGateFrame();
    this.drawMultiplierBadge();
    this.add(this.frameGraphics);
    this.add(this.trimGraphics);
    this.add(this.multiplierPlate);
    this.add(this.multiplierPrefix);

    scene.add.existing(this);
    this.refreshVisualState(scene.time.now);
  }

  /**
   * Sets the scroll speed (called by GameScene)
   */
  setScrollSpeed(speed: number): void {
    this.scrollSpeed = speed;
  }

  /**
   * Updates gate position each frame
   */
  update(time: number, delta: number): void {
    if (!this.isActive) return;
    if (!this.isActivated) {
      this.refreshVisualState(time);
    }

    const movement = (this.scrollSpeed * delta) / 1000;
    this.y += movement;

    if (this.y > GAME_HEIGHT + Gate.HEIGHT) {
      this.destroy();
    }
  }

  /**
   * Activates the gate when player passes through
   */
  activate(currentArmySize: number): number {
    if (this.isActivated || !this.isActive) return 0;

    this.isActivated = true;

    const newArmySize = this.applyOperation(currentArmySize, this.operation);
    const soldiersDelta = newArmySize - currentArmySize;
    const scoreFactor = this.getScoreFactor(this.operation);

    this.playActivationEffect();
    // Legacy signature compatibility:
    // (multiplierLikeValue, soldiersDelta, scoreFactor, operation)
    this.emit('activated', scoreFactor, soldiersDelta, scoreFactor, this.operation);

    return soldiersDelta;
  }

  private playActivationEffect(): void {
    this.approachIntensity = 0;
    this.scene.tweens.killTweensOf(this);

    const ring = this.scene.add.graphics().setPosition(this.x, this.y).setDepth(this.depth + 1);
    ring.lineStyle(4, this.accentColor, 0.9);
    ring.strokeCircle(0, 0, 20);
    this.scene.tweens.add({
      targets: ring,
      scaleX: 3.2,
      scaleY: 3.2,
      alpha: 0,
      duration: 280,
      ease: 'Cubic.easeOut',
      onComplete: () => ring.destroy(),
    });

    getParticlePool()?.spawnBurst(this.x, this.y, 'square', this.accentColor, 10, {
      minSize: 3,
      maxSize: 7,
      minVelocity: 70,
      maxVelocity: 150,
      minDuration: 180,
      maxDuration: 340,
      alpha: 0.9,
    });
    this.scene.tweens.add({
      targets: this,
      scaleX: EFFECTS.gateActivation.popScale,
      scaleY: EFFECTS.gateActivation.popScale,
      duration: EFFECTS.gateActivation.popDuration,
      ease: 'Back.easeOut',
      yoyo: true,
    });

    this.scene.tweens.add({
      targets: this,
      alpha: 0,
      duration: 120,
      ease: 'Linear',
      onComplete: () => {
        this.isActive = false;
        this.setVisible(false);
      },
    });
  }

  /**
   * Deactivates gate without activation effect (when another gate in row is chosen)
   */
  deactivate(): void {
    if (!this.isActive) return;

    this.isActive = false;

    this.scene.tweens.add({
      targets: this,
      alpha: 0.3,
      scaleX: 0.9,
      scaleY: 0.9,
      duration: 200,
      ease: 'Power2',
      onComplete: () => {
        this.setVisible(false);
        this.destroy();
      },
    });
  }

  /**
   * Sets approach highlight intensity (0-1)
   */
  setApproachIntensity(intensity: number): void {
    this.approachIntensity = Phaser.Math.Clamp(intensity, 0, 1);
    if (!this.isActivated && this.isActive && this.scene?.time) {
      this.refreshVisualState(this.scene.time.now);
    }
  }

  /**
   * Returns a numeric gate strength used by existing ranking logic.
   */
  getMultiplier(): number {
    return this.getScoreFactor(this.operation);
  }

  getOperation(): GateOperation {
    return this.operation;
  }

  getIsActive(): boolean {
    return this.isActive;
  }

  getIsActivated(): boolean {
    return this.isActivated;
  }

  getCollisionBounds(): { x: number; y: number; width: number; height: number } {
    const width = Math.max(8, Gate.WIDTH - GATE_LAYOUT.collisionInsetX * 2);
    const height = Math.max(8, Gate.HEIGHT - GATE_LAYOUT.collisionInsetY * 2);
    return {
      x: this.x - width / 2,
      y: this.y - height / 2,
      width,
      height,
    };
  }

  private drawGateFrame(): void {
    const halfW = Gate.WIDTH / 2;
    const halfH = Gate.HEIGHT / 2;

    this.frameGraphics.clear();
    drawChamferedPanel(
      this.frameGraphics,
      -halfW,
      -halfH,
      Gate.WIDTH,
      Gate.HEIGHT,
      0x07131F,
      this.accentColor,
      0.88,
      11
    );
    this.frameGraphics.fillStyle(this.accentColor, 0.08);
    this.frameGraphics.fillRect(-halfW + 12, -halfH + 9, Gate.WIDTH - 24, Gate.HEIGHT - 18);

    this.trimGraphics.clear();
    this.trimGraphics.fillStyle(this.accentColor, 0.95);
    this.trimGraphics.fillRect(-halfW + 12, -halfH + 5, 38, 3);
    this.trimGraphics.fillRect(halfW - 50, halfH - 8, 38, 3);
    this.trimGraphics.lineStyle(1, 0xD7F7FF, 0.24);
    this.trimGraphics.lineBetween(-halfW + 14, 0, halfW - 14, 0);
  }

  private drawMultiplierBadge(): void {
    this.multiplierPlate.clear();
    this.multiplierPlate.fillStyle(0x02070D, 0.76);
    this.multiplierPlate.fillRoundedRect(-37, -19, 74, 38, 7);
    this.multiplierPlate.lineStyle(1, this.accentColor, 0.82);
    this.multiplierPlate.strokeRoundedRect(-37, -19, 74, 38, 7);
  }

  private refreshVisualState(timeMs: number): void {
    const pulse = 1 + Math.sin((timeMs + this.pulseOffsetMs) * 0.008) * 0.006;
    const approachScale = 1 + this.approachIntensity * 0.012;
    this.setScale(pulse * approachScale);

    this.frameGraphics.setAlpha(0.9 + this.approachIntensity * 0.1);
    this.trimGraphics.setAlpha(0.9 + this.approachIntensity * 0.1);
    this.multiplierPlate.setAlpha(0.62 + this.approachIntensity * 0.18);
    this.multiplierPrefix.setAlpha(0.95 + this.approachIntensity * 0.05);
  }

  private getOperationColor(operation: GateOperation): number {
    if (operation.kind === 'sub') return COLORS.enemy;
    if (operation.kind === 'add') return 0x00D4FF;
    return COLORS.player;
  }

  private getOperationLabel(operation: GateOperation): string {
    if (operation.kind === 'mul') return `×${operation.value}`;
    if (operation.kind === 'add') return `+${operation.value}`;
    return `-${operation.value}`;
  }

  private applyOperation(currentArmySize: number, operation: GateOperation): number {
    if (operation.kind === 'mul') {
      return getEffectiveMultiplier(currentArmySize, operation.value);
    }
    if (operation.kind === 'add') {
      return Math.min(ARMY_HARD_CAP, currentArmySize + operation.value);
    }
    return Math.max(0, currentArmySize - operation.value);
  }

  private getScoreFactor(operation: GateOperation): number {
    if (operation.kind === 'mul') return operation.value;
    if (operation.kind === 'add') return 1 + operation.value / 2;
    return Math.max(0.5, 1 - operation.value / 3);
  }

  private resolveOperation(config: GateConfig): GateOperation {
    if (config.operation) {
      return config.operation;
    }

    const rawMultiplier = Math.round(config.multiplier ?? 1);
    const allowed: Array<1 | 2 | 3 | 5> = [1, 2, 3, 5];
    const resolved = allowed.includes(rawMultiplier as 1 | 2 | 3 | 5)
      ? (rawMultiplier as 1 | 2 | 3 | 5)
      : 1;
    return { kind: 'mul', value: resolved };
  }
}
